import {
  CancelOutlined,
  CreditCardOutlined,
  EditOutlined,
  Error,
  ExpandLess,
  ExpandMore,
  History as HistoryIcon,
  LocalPhoneOutlined,
  ReportProblemOutlined,
  TaskAltOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Snackbar, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { EProductSection } from "src/commons/interfaces/product";
import { useModals } from "../../context/ModalContext";
import { initialBenefitsFetch } from "../../permissions/benefits/redux/benefits.init";
import { cancelMySubscriptionInfo, ESubscriptionStatus, formatUSPhone, getPlanById } from "../../utils";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { formatCompactBillingMethod } from "../subscriptions/actions/get-billing-vaults.actions";
import { useCompanyBillingVaultsQuery } from "../subscriptions/hooks/useCompanyBillingVaultsQuery";
import { EQueryKeys, useMySubscriptionInfoSync } from "../subscriptions/hooks/useMySubscriptionInfo.query";
import { useRetrySubscriptionPayment } from "../subscriptions/hooks/useRetrySubscription.mutation";
import { useSubscriptionInfoQuery } from "../subscriptions/hooks/useSubscriptionInfoQuery";
import { SUBSCRIPTION_KEYS } from "../subscriptions/queries/subscription.queries";
import { SubscriptionPaymentHistoryModal } from "../superadmin/billing/components/SubscriptionPaymentHistoryModal";
import { colors, alphaVar } from "../ui/colors";
import { UpdatePaygPaymentMethod } from "../UpdatePaygPaymentMethod";
import { useCompanyNumberQuery } from "../virtual-receptionist/widget/components/NumbersConfiguration/hooks/useCompanyNumberQuery";

export const EServiceBillingPeriodStatus = {
  OPEN: "OPEN",
  CALCULATED: "CALCULATED",
  PROCESSING: "PROCESSING",
  PAID: "PAID",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  PAST_DUE: "PAST_DUE",
  CANCELED: "CANCELED",
};

export const getLatestBillingPeriodInfo = (subscription) => {
  const billingPeriods = subscription?.billingPeriods;
  if (!Array.isArray(billingPeriods) || billingPeriods.length === 0) {
    return { hasPendingPayment: false, latestPeriod: null };
  }

  const latestPeriod = billingPeriods[0];
  const status = (latestPeriod?.status || "").toUpperCase();
  const isPaid = status === "PAID";
  const isCanceled = status === "CANCELED" || status === "CANCELLED";
  const hasPendingPayment = !isPaid && !isCanceled;

  return {
    hasPendingPayment,
    latestPeriod,
    status,
    isFailed: status === "PAYMENT_FAILED" || status === "PAST_DUE" || status === "FAILED",
    isCalculated: status === "CALCULATED" || status === "OPEN" || status === "PROCESSING" || status === "PENDING",
  };
};

const getSubscriptionDisplayName = (subscriptionItem, isParent = false) => {
  const productName = subscriptionItem?.plan?.product?.name;
  const section = subscriptionItem?.plan?.product?.section;

  if (productName) return productName;
  if (!isParent && section === "flow_mobile") return "Flow Mobile";

  return "Unknown subscription";
};

const getBenefitLabel = (benefit) => {
  const limitedUsage = benefit?.benefit?.config?.limitedUsage;
  const isMonthlyReset = benefit?.benefit?.config?.isMonthlyReset;

  if (limitedUsage) return `${limitedUsage}${isMonthlyReset ? "/month" : ""}`;
  return null;
};

const parseSubscriptionMeta = (subscriptionItem) => {
  const meta = subscriptionItem?.meta;

  if (!meta) return {};
  if (typeof meta === "object") return meta;

  try {
    return JSON.parse(meta);
  } catch (_error) {
    return {};
  }
};

const getSubscriptionLineNumber = (subscriptionItem, companyNumbersByUid, fallbackCompanyNumber = null) => {
  const assignedCompanyNumber = subscriptionItem?.externalId ? companyNumbersByUid?.get(subscriptionItem.externalId) : null;
  const assignedPhoneNumber = assignedCompanyNumber?.phoneNumber || fallbackCompanyNumber?.phoneNumber;

  if (assignedPhoneNumber) {
    return formatUSPhone(assignedPhoneNumber) || assignedPhoneNumber;
  }

  const meta = parseSubscriptionMeta(subscriptionItem);
  const rawNumber =
    meta?.phoneNumber ||
    meta?.phone ||
    meta?.number ||
    meta?.lineNumber ||
    meta?.serviceReference ||
    meta?.porting?.numberToPort ||
    meta?.numberToPort ||
    meta?.buying?.phoneNumber;

  if (!rawNumber) return null;

  return formatUSPhone(rawNumber) || rawNumber;
};

const getSubscriptionStatusChip = (status) => {
  switch (status) {
    case ESubscriptionStatus.COMPLETED:
    case ESubscriptionStatus.ACTIVE:
    case "active":
      return { label: "ACTIVE", bgcolor: alphaVar(colors.success.base, 0.12), color: colors.success.base };
    case ESubscriptionStatus.SUSPENDED:
    case ESubscriptionStatus.FAILED:
    case "suspended":
    case "failed":
      return { label: "FAILED", bgcolor: alphaVar(colors.danger.base, 0.12), color: colors.danger.base };
    case ESubscriptionStatus.CANCELLED:
    case "cancelled":
      return { label: "CANCELLED", bgcolor: alphaVar(colors.danger.base, 0.12), color: colors.danger.base };
    default:
      return { label: "PENDING", bgcolor: alphaVar(colors.info.base, 0.12), color: colors.info.base };
  }
};

const BenefitListItem = ({ benefit }) => {
  const normalizedBenefit = benefit?.benefit || benefit?.Benefit || benefit;
  const usageLabel = getBenefitLabel({ benefit: normalizedBenefit });

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.5,
        fontSize: 0.78,
        borderRadius: 1.5,
        backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
        color: "var(--fx-surface-textsecondary, #64748B)",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 10,
          lineHeight: 1,
          whiteSpace: "nowrap",
          color: "var(--fx-surface-textsecondary, #64748B)",
        }}
      >
        {normalizedBenefit?.name || "Unnamed benefit"}
      </Typography>

      {usageLabel && (
        <Chip
          size="small"
          label={usageLabel}
          sx={{
            height: 18,
            "& .MuiChip-label": {
              px: 0.7,
              fontSize: "0.64rem",
              fontWeight: 700,
            },
            bgcolor: alphaVar(colors.orange.base, 0.12),
            color: colors.orange.base,
          }}
        />
      )}
    </Box>
  );
};

const normalizeSubscriptionBenefits = (subscriptionItem) => {
  const companyBenefits = subscriptionItem?.companyBenefits ?? [];

  if (companyBenefits.length > 0) {
    return companyBenefits.map((item) => {
      const benefit = item?.benefit || item?.Benefit || item;

      return {
        id: item?.id || benefit?.id,
        benefit,
        CompanyBenefits: item?.CompanyBenefits ||
          item?.companyBenefits || {
            usageCount: item?.usageCount ?? 0,
          },
      };
    });
  }

  const productBenefits =
    subscriptionItem?.plan?.product?.benefits || subscriptionItem?.plan?.product?.Benefits || subscriptionItem?.plan?.benefits || [];

  return productBenefits.map((benefit) => ({
    id: benefit.id,
    benefit,
    CompanyBenefits: {
      usageCount: 0,
    },
  }));
};

export const SubscriptionSection = ({
  title,
  subscriptionItem,
  isParent = false,
  retryingSubscriptionId,
  onRetryPayment,
  canUpdatePlan = true,
  onlyBenefits = false,
  hidePlanHeader = false,
}) => {
  const [dialogType, setDialogType] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const { showBoundary } = useErrorBoundary();
  const [fallbackBenefits, setFallbackBenefits] = useState([]);
  const [openBenefits, setOpenBenefits] = useState(false);

  useEffect(() => {
    const loadFallbackBenefits = async () => {
      if (!subscriptionItem) return;

      const hasCompanyBenefits = subscriptionItem?.companyBenefits?.length > 0;
      const hasProductBenefits = subscriptionItem?.plan?.product?.benefits?.length > 0;

      if (hasCompanyBenefits || hasProductBenefits) return;

      const currentPlan = subscriptionItem?.plan;
      if (!currentPlan) return;

      const planId = currentPlan.isFreeTrial || currentPlan.isCustom ? currentPlan.id : currentPlan.paypalPlanId;

      if (!planId) return;

      try {
        const planDetail = await getPlanById(planId, currentPlan.isFreeTrial, currentPlan.isCustom);

        const productBenefits = planDetail?.product?.benefits ?? [];

        setFallbackBenefits(
          productBenefits.map((benefit) => ({
            id: benefit.id,
            benefit,
            CompanyBenefits: {
              usageCount: 0,
            },
          })),
        );
      } catch (error) {
        console.error("Failed to load fallback benefits:", error);
      }
    };

    loadFallbackBenefits();
  }, [subscriptionItem]);

  if (!subscriptionItem) return null;

  const planName = getSubscriptionDisplayName(subscriptionItem, isParent);
  const normalizedBenefits = normalizeSubscriptionBenefits(subscriptionItem);

  const benefits = normalizedBenefits.length > 0 ? normalizedBenefits : fallbackBenefits;

  const status = subscriptionItem?.status;
  const subscriptionId = subscriptionItem?.id;
  const isSuspended = status === ESubscriptionStatus.SUSPENDED || status === ESubscriptionStatus.FAILED;
  const isRetrying = retryingSubscriptionId === subscriptionId;
  const isFree = subscriptionItem?.paypalId === "free_trial";
  const token = sessionStorage.getItem("token");
  const isCancelled = status === ESubscriptionStatus.CANCELLED;

  const handleOpenDialog = (type = "cancel") => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleCancelMySubscription = async () => {
    try {
      setLoading(true);
      await cancelMySubscriptionInfo(token, subscriptionItem?.id);
    } catch (error) {
      showBoundary(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipConfig = (currentStatus) => {
    switch (currentStatus) {
      case ESubscriptionStatus.COMPLETED:
      case ESubscriptionStatus.ACTIVE:
      case "active":
        return { label: "Active", bgColor: colors.orange.base, color: colors.orange.base };
      case ESubscriptionStatus.SUSPENDED:
      case "suspended":
        return {
          label: "Suspended",
          bgColor: isParent ? "rgba(245,158,11,0.24)" : alphaVar(colors.warning.base, 0.12),
          color: isParent ? "white" : colors.warning.base,
        };
      case ESubscriptionStatus.CANCELLED:
      case "cancelled":
        return {
          label: "Cancelled",
          bgColor: isParent ? "rgba(239,68,68,0.22)" : alphaVar(colors.danger.base, 0.12),
          color: isParent ? "white" : colors.danger.base,
        };
      default:
        return {
          label: "Inactive",
          bgColor: isParent ? "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.16)" : alphaVar(colors.gray[400], 0.12),
          color: isParent ? "white" : colors.gray[400],
        };
    }
  };

  const billingInfo = getLatestBillingPeriodInfo(subscriptionItem);
  const { hasPendingPayment, latestPeriod, isFailed: isBillingFailed, status: billingStatus } = billingInfo;

  const statusChip = getStatusChipConfig(status);
  if (isFree) return null;

  return (
    <Card
      sx={{
        mb: onlyBenefits ? 0 : 2,
        borderRadius: 3,
        boxShadow: "none",
        overflow: "hidden",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      {!onlyBenefits && (
        <Box
          sx={{
            color: isParent ? "white" : colors.gray[500],
          }}
        >
          <Box
            onClick={() => setOpenBenefits((prev) => !prev)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: 1.5,
              borderBottom: openBenefits ? `1px solid ${alphaVar(colors.gray[400], 0.1)}` : "none",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: isParent ? colors.gray[500] : colors.gray[500],
                }}
              >
                {hidePlanHeader ? title || "Your Benefits" : planName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {hasPendingPayment ? (
                <Chip
                  size="small"
                  label={isBillingFailed ? "PAYMENT FAILED" : `PENDING (${billingStatus})`}
                  sx={{
                    bgcolor: isBillingFailed ? alphaVar(colors.danger.base, 0.12) : "var(--fx-colors-yellow-100, #FFF9C2)",
                    color: isBillingFailed ? colors.danger.base : "#B45309",
                    fontWeight: 800,
                    fontSize: "0.68rem",
                    height: 24,
                    border: `1px solid ${isBillingFailed ? alphaVar(colors.danger.base, 0.3) : "#FCD34D"}`,
                  }}
                />
              ) : !!status ? (
                <TaskAltOutlined
                  sx={{
                    color: colors.orange.base,
                    fontSize: "1.5rem",
                  }}
                />
              ) : null}

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenBenefits((prev) => !prev);
                }}
                sx={{
                  color: colors.orange.base,
                  transform: openBenefits ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <ExpandMore />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      <Collapse in={onlyBenefits || openBenefits} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          {hasPendingPayment && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.25,
                px: 1.75,
                borderRadius: 1.5,
                bgcolor: isBillingFailed ? alphaVar(colors.danger.base, 0.05) : "var(--fx-colors-yellow-50, #FFFDEB)",
                borderLeft: `3px solid ${isBillingFailed ? colors.danger.base : "#F59E0B"}`,
                borderTop: `1px solid ${isBillingFailed ? alphaVar(colors.danger.base, 0.15) : "#FEF08A"}`,
                borderRight: `1px solid ${isBillingFailed ? alphaVar(colors.danger.base, 0.15) : "#FEF08A"}`,
                borderBottom: `1px solid ${isBillingFailed ? alphaVar(colors.danger.base, 0.15) : "#FEF08A"}`,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              {isBillingFailed ? (
                <ReportProblemOutlined sx={{ color: colors.danger.base, fontSize: 18, flexShrink: 0 }} />
              ) : (
                <WarningAmberOutlined sx={{ color: "#D97706", fontSize: 18, flexShrink: 0 }} />
              )}
              <Typography sx={{ fontSize: "0.78rem", color: isBillingFailed ? "#991B1B" : "#854D0E", fontWeight: 500, flex: 1, minWidth: 200 }}>
                <strong>{isBillingFailed ? "Payment Failed" : "Pending Payment"}</strong>: Latest period{" "}
                {latestPeriod?.periodStart && latestPeriod?.periodEnd
                  ? `(${new Date(latestPeriod.periodStart).toLocaleDateString()} – ${new Date(latestPeriod.periodEnd).toLocaleDateString()})`
                  : ""}{" "}
                ({billingStatus}) ·{" "}
                <strong>
                  ${latestPeriod?.totalAmount || "0.00"} {latestPeriod?.currencyCode || "USD"}
                </strong>
                . Check <strong>Billing History</strong> for details.
              </Typography>
            </Box>
          )}
          {!hidePlanHeader && (
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: ".78rem",
                color: colors.orange.base,
                mb: 1,
              }}
            >
              {title || "Your Benefits"}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {benefits.map((benefit) => (
              <BenefitListItem key={`${planName}-${benefit.id}`} benefit={benefit} />
            ))}
          </Box>

          {!onlyBenefits && !isParent && !isCancelled && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "end", width: "100%" }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleOpenDialog("cancel")}
                  sx={{
                    borderColor: colors.orange.base,
                    color: colors.orange.base,
                    fontWeight: 500,
                    height: 35,
                    fontSize: "12px",
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: colors.orange.base,
                      bgcolor: colors.orange[50],
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{ fontWeight: 700, color: colors.orange.base }}>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.gray[400] }}>Lose access to premium benefits at the end of period.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Discard</Button>
          <Button
            onClick={() => {
              handleCancelMySubscription();
              handleCloseDialog();
            }}
            variant="contained"
            sx={{ bgcolor: colors.danger.base, color: "white", "&:hover": { bgcolor: colors.danger[100] } }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const UpdatePaygPaymentModalContent = ({ subscription, vault, companyId, currentMethodLabel, queryClient, dispatch, setSnackbar }) => {
  const { closeByKey } = useModals();
  const modalKey = `update-payg-payment-${subscription?.id}`;
  const [showUpdateForm, setShowUpdateForm] = useState(!vault);

  return (
    <Box sx={{ p: 2.5, bgcolor: "var(--fx-surface-base, #FFFFFF)", height: "100%", overflowY: "auto" }}>
      {vault ? (
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "grey.200",
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CreditCardOutlined sx={{ color: colors.orange.base, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
                Current Payment Method Details
              </Typography>
            </Box>
            <Chip
              label={vault.status || "ACTIVE"}
              size="small"
              sx={{
                bgcolor: vault.status === "ACTIVE" ? "var(--fx-colors-success-100, #D1FAE5)" : "var(--fx-colors-yellow-100, #FFF9C2)",
                color: vault.status === "ACTIVE" ? "#15803D" : "#B45309",
                fontWeight: 800,
                fontSize: "0.68rem",
                borderRadius: "12px",
              }}
            />
          </Box>

          <Divider sx={{ mb: 2, borderColor: "var(--fx-surface-border, #E2E8F0)" }} />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2 }}>
            {vault.methodType === "card" || vault.brand || vault.lastDigits ? (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                    Card Brand
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
                    {vault.brand || "Card"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                    Last 4 Digits
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)", fontFamily: "monospace" }}>
                    •••• {vault.lastDigits || "----"}
                  </Typography>
                </Box>
                {vault.expiry && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                      Expires
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
                      {vault.expiry}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ gridColumn: "span 2" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                  Account Email / Name
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
                  {vault.paypalEmail || vault.paypalAccountName || "PayPal Account"}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                Gateway Provider
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)", textTransform: "capitalize" }}>
                {vault.provider || "Stripe"}
              </Typography>
            </Box>

            {vault.activatedAt && (
              <Box sx={{ gridColumn: "span 2" }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
                  Activated Date
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                  {dayjs(vault.activatedAt).format("MMM DD, YYYY HH:mm")}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      ) : null}

      {vault && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: showUpdateForm ? 2.5 : 0 }}>
          <Button
            variant={showUpdateForm ? "outlined" : "contained"}
            onClick={() => setShowUpdateForm((prev) => !prev)}
            startIcon={showUpdateForm ? <ExpandLess /> : <CreditCardOutlined />}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              px: 3,
              py: 1,
              bgcolor: showUpdateForm ? "transparent" : colors.orange.base,
              borderColor: colors.orange.base,
              color: showUpdateForm ? colors.orange.base : "#FFFFFF",
              "&:hover": {
                bgcolor: showUpdateForm ? colors.orange[50] : colors.orange[100],
                borderColor: colors.orange.base,
              },
            }}
          >
            {showUpdateForm ? "Hide Payment Form" : "Replace Payment Method"}
          </Button>
        </Box>
      )}

      <Collapse in={showUpdateForm} timeout="auto" unmountOnExit={false}>
        <Box sx={{ pt: vault ? 1 : 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 1.5 }}>
            {vault ? "Select New Payment Method" : "Add Payment Method"}
          </Typography>

          <UpdatePaygPaymentMethod
            key={subscription?.id}
            subscriptionId={String(subscription?.id)}
            companyId={companyId}
            planId={subscription?.planId ?? subscription?.plan?.id}
            currentMethodLabel={null}
            onSuccess={async (result) => {
              await queryClient.invalidateQueries({
                queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO],
              });
              await queryClient.invalidateQueries({
                queryKey: SUBSCRIPTION_KEYS.self("flow_mobile"),
              });
              await queryClient.invalidateQueries({
                queryKey: SUBSCRIPTION_KEYS.billingVaults,
              });
              dispatch(initialBenefitsFetch());

              const pendingFailed = Number(result?.data?.pendingCharges?.failed) > 0;
              setSnackbar({
                open: true,
                message:
                  result?.message ||
                  (pendingFailed
                    ? "Payment method updated, but a pending charge could not be completed. It will be retried automatically."
                    : "Payment method updated successfully."),
                severity: pendingFailed ? "warning" : "success",
              });
              closeByKey(modalKey);
            }}
            onError={(message) => {
              setSnackbar({
                open: true,
                message: message || "Failed to update payment method",
                severity: "error",
              });
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export const SecondarySubscriptionInfo = () => {
  const { subscriptionInfo, subscription, activeCompanyId, company } = useSelector((state) => state.userState);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showBoundary } = useErrorBoundary();
  const { data: companyNumbers = [] } = useCompanyNumberQuery();
  const { data: flowMobileSubscriptionInfo } = useSubscriptionInfoQuery({
    skipPayPalFetch: true,
    section: "flow_mobile",
  });
  const { data: billingVaults = [] } = useCompanyBillingVaultsQuery();

  const cachedData = useMemo(() => {
    const saved = localStorage.getItem("cached_subscriptions");
    const parsed = saved ? JSON.parse(saved) : null;

    if (subscriptionInfo?.subscriptions?.length > 0) {
      localStorage.setItem("cached_subscriptions", JSON.stringify(subscriptionInfo));
    }

    return parsed;
  }, [subscriptionInfo]);

  const subscriptions = subscriptionInfo?.subscriptions ?? cachedData?.subscriptions ?? [];
  const companyNumbersByUid = useMemo(() => {
    return new Map(
      companyNumbers.map((companyNumber) => {
        return [companyNumber.uid, companyNumber];
      }),
    );
  }, [companyNumbers]);
  const flowMobileSubscriptionsById = useMemo(() => {
    const flowMobileSubscriptions = flowMobileSubscriptionInfo?.data?.subscriptions ?? [];

    return new Map(
      flowMobileSubscriptions
        .map((item) => item?.subscription)
        .filter(Boolean)
        .map((item) => {
          return [item.id, item];
        }),
    );
  }, [flowMobileSubscriptionInfo]);
  const billingVaultsBySubscriptionId = useMemo(() => {
    return new Map((billingVaults || []).map((vault) => [vault.subscriptionId, vault]));
  }, [billingVaults]);
  const [retryingSubscriptionId, setRetryingSubscriptionId] = useState(null);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const companyId = activeCompanyId || company?.id;

  useMySubscriptionInfoSync();

  const { mutate: retryPayment } = useRetrySubscriptionPayment({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO] });
      dispatch(initialBenefitsFetch());
      setRetryingSubscriptionId(null);
    },
    onError: () => setRetryingSubscriptionId(null),
  });

  const handleRetryPayment = (id) => {
    if (!id) return;
    setRetryingSubscriptionId(id);
    retryPayment(id);
  };

  const handleConfirmCancel = async () => {
    if (!subscriptionToCancel || isCancelling) return;

    try {
      setIsCancelling(true);

      const token = sessionStorage.getItem("token");

      await cancelMySubscriptionInfo(token, subscriptionToCancel.id);

      await queryClient.invalidateQueries({
        queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO],
      });

      dispatch(initialBenefitsFetch());

      setSubscriptionToCancel(null);

      setSnackbar({
        open: true,
        message: "Subscription cancelled successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to cancel subscription:", error);

      setSnackbar({
        open: true,
        message: "Failed to cancel subscription",
        severity: "error",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const { activeSubscriptions, cancelledSubscriptions } = useMemo(() => {
    if (!Array.isArray(subscriptions)) return { activeSubscriptions: [], cancelledSubscriptions: [] };

    const mainId = subscription?.id ?? cachedData?.subscription?.id;

    const others = subscriptions.filter((item) => {
      const currentId = item?.subscription?.id;
      const isMain = currentId === mainId;
      const isFree = item?.subscription?.paypalId === "free_trial";
      const isInvalid = !currentId;
      const isFlowMobile = item?.subscription?.plan?.product?.section === EProductSection.FLOW_MOBILE;

      return !isMain && !isFree && !isInvalid && isFlowMobile;
    });

    return {
      activeSubscriptions: others.filter((item) => item?.subscription?.status?.toUpperCase() !== "CANCELLED"),
      cancelledSubscriptions: others.filter((item) => item?.subscription?.status?.toUpperCase() === "CANCELLED"),
    };
  }, [subscriptions, subscription, cachedData]);

  const singleAvailableCompanyNumber = useMemo(() => {
    if (activeSubscriptions.length !== 1) return null;

    const availableNumbers = companyNumbers.filter((companyNumber) => {
      return ["ACTIVE", "PENDING", "INACTIVE"].includes(companyNumber?.status) && companyNumber?.phoneNumber;
    });

    return availableNumbers.length === 1 ? availableNumbers[0] : null;
  }, [activeSubscriptions, companyNumbers]);

  if (activeSubscriptions.length === 0 && cancelledSubscriptions.length === 0) {
    return null;
  }

  const handleCloseCancelDialog = (_event, reason) => {
    if (isCancelling) return;

    setSubscriptionToCancel(null);
  };

  return (
    <>
      {/*ACTIVE */}
      {activeSubscriptions.length > 0 && (
        <Card
          sx={{
            mb: 2,
            borderRadius: 3,
            boxShadow: "none",
            overflow: "hidden",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          }}
        >
          <Box
            sx={{
              padding: 1.5,
              borderBottom: `1px solid ${alphaVar(colors.gray[400], 0.1)}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1rem",
                color: colors.orange.base,
                alignItems: "center",
              }}
            >
              Flow Mobile Subscriptions
            </Typography>
          </Box>

          <Box sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: "minmax(190px, 1.2fr) minmax(180px, 1fr) minmax(120px, 0.65fr) 64px",
                gap: 2,
                px: 2,
                py: 1.1,
                borderBottom: `1px solid ${alphaVar(colors.gray[400], 0.14)}`,
              }}
            >
              {["Phone Number", "Plan", "Status", "Benefits"].map((label) => (
                <Typography
                  key={label}
                  sx={{
                    color: "var(--fx-surface-text, #0F172A)",
                    fontWeight: 800,
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: 0,
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            {activeSubscriptions.map((item) => {
              const flowMobileSubscription = flowMobileSubscriptionsById.get(item?.subscription?.id);
              const subscriptionWithLineReference = {
                ...item?.subscription,
                externalId: item?.subscription?.externalId ?? flowMobileSubscription?.externalId,
                meta: item?.subscription?.meta ?? flowMobileSubscription?.meta,
              };
              const status = item?.subscription?.status;
              const isSuspended = status === ESubscriptionStatus.SUSPENDED || status === ESubscriptionStatus.FAILED;
              const lineNumber = getSubscriptionLineNumber(subscriptionWithLineReference, companyNumbersByUid, singleAvailableCompanyNumber);
              const statusChip = getSubscriptionStatusChip(status);

              const itemBillingInfo = getLatestBillingPeriodInfo(item?.subscription);
              const { hasPendingPayment: itemHasPending, isFailed: itemIsFailed, status: itemBillingStatus } = itemBillingInfo;
              const itemVault = billingVaultsBySubscriptionId.get(item?.subscription?.id);
              const billingMethodLabel = formatCompactBillingMethod(itemVault);

              return (
                <Accordion
                  key={item?.subscription?.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    background: "var(--fx-surface-base, #FFFFFF)",
                    borderRadius: 1.5,
                    mb: 1,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: itemHasPending ? "#D97706" : isSuspended ? colors.danger.base : colors.orange.base }} />}
                    sx={{
                      px: 2,
                      minHeight: 58,
                      "& .MuiAccordionSummary-content": {
                        my: 1,
                        minWidth: 0,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr auto",
                          md: "minmax(190px, 1.2fr) minmax(180px, 1fr) minmax(120px, 0.65fr) 42px",
                        },
                        gap: { xs: 1, md: 2 },
                        alignItems: "center",
                        width: "100%",
                        mr: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <LocalPhoneOutlined sx={{ color: colors.orange.base, fontSize: 18, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: "0.86rem",
                              color: lineNumber ? "var(--fx-surface-text, #0F172A)" : "var(--fx-surface-texttertiary, #94A3B8)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {lineNumber || "Line pending"}
                          </Typography>
                          <Typography sx={{ display: { xs: "block", md: "none" }, fontSize: "0.68rem", fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                            {getSubscriptionDisplayName(item?.subscription)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            color: "var(--fx-surface-textsecondary, #64748B)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getSubscriptionDisplayName(item?.subscription)}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={itemIsFailed || isSuspended ? "FAILED" : itemHasPending ? "PENDING" : statusChip.label}
                        sx={{
                          justifySelf: { xs: "end", md: "start" },
                          height: 24,
                          bgcolor: itemIsFailed || isSuspended ? "var(--fx-colors-danger-100, #FEE2E2)" : itemHasPending ? "var(--fx-colors-yellow-100, #FFF9C2)" : statusChip.bgcolor,
                          color: itemIsFailed || isSuspended ? "#B91C1C" : itemHasPending ? "#B45309" : statusChip.color,
                          border: itemIsFailed || isSuspended ? "1px solid #FCA5A5" : itemHasPending ? "1px solid #FCD34D" : "none",
                          fontWeight: 800,
                          "& .MuiChip-label": {
                            px: 0.9,
                            fontSize: "0.68rem",
                          },
                        }}
                      />

                      <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
                        {itemHasPending ? (
                          <WarningAmberOutlined sx={{ color: itemIsFailed ? colors.danger.base : "#D97706", fontSize: 20 }} />
                        ) : isSuspended ? (
                          <Error sx={{ color: colors.danger.base, fontSize: 20 }} />
                        ) : (
                          <TaskAltOutlined sx={{ color: colors.orange.base, fontSize: 20 }} />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ bgcolor: "var(--fx-surface-base, #FFFFFF)", p: 0 }}>
                    <SubscriptionSection
                      subscriptionItem={item?.subscription}
                      onRetryPayment={handleRetryPayment}
                      retryingSubscriptionId={retryingSubscriptionId}
                      onlyBenefits={true}
                    />

                    {isSuspended && (
                      <Typography
                        sx={{
                          color: colors.danger.base,
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          px: 1.5,
                          pb: 1.5,
                          lineHeight: 1.3,
                        }}
                      >
                        There was a problem processing the payment for this subscription. Update your payment method or view Billing History for
                        details.
                      </Typography>
                    )}

                    <Box
                      sx={{
                        px: 1.5,
                        pb: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
                        <BaseModalComponent
                          modalKey={`update-payg-payment-${item?.subscription?.id}`}
                          modalLabel={`Update Payment Method · ${getSubscriptionDisplayName(item?.subscription)}`}
                          defaultSizes={{ width: 620, height: 680 }}
                          elementTrigger={
                            <Box
                              role="button"
                              tabIndex={0}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1.25,
                                px: 1.75,
                                py: 0.75,
                                borderRadius: 2.5,
                                bgcolor: isSuspended || itemIsFailed ? alphaVar(colors.danger.base, 0.08) : "var(--fx-surface-base, #FFFFFF)",
                                border: "1px solid",
                                borderColor: isSuspended || itemIsFailed ? alphaVar(colors.danger.base, 0.4) : "var(--fx-surface-border, #E2E8F0)",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                cursor: "pointer",
                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  borderColor: isSuspended || itemIsFailed ? colors.danger.base : colors.orange.base,
                                  bgcolor: isSuspended || itemIsFailed ? alphaVar(colors.danger.base, 0.12) : "var(--fx-surface-base, #FFFFFF)",
                                  boxShadow: `0 4px 12px ${
                                    isSuspended || itemIsFailed ? alphaVar(colors.danger.base, 0.15) : "rgba(251, 142, 0, 0.15)"
                                  }`,
                                  transform: "translateY(-1px)",
                                  "& .update-text": {
                                    color: isSuspended || itemIsFailed ? colors.danger.base : colors.orange.base,
                                  },
                                },
                              }}
                            >
                              <CreditCardOutlined
                                sx={{
                                  fontSize: 18,
                                  color: isSuspended || itemIsFailed ? colors.danger.base : "#475569",
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.78rem",
                                  color: isSuspended || itemIsFailed ? colors.danger.base : "#1E293B",
                                  fontWeight: 700,
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {billingMethodLabel || "No payment method registered"}
                              </Typography>

                              <Divider orientation="vertical" flexItem sx={{ my: 0.25, borderColor: "var(--fx-surface-border, #E2E8F0)" }} />

                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography
                                  className="update-text"
                                  sx={{
                                    fontSize: "0.74rem",
                                    fontWeight: 700,
                                    color: isSuspended || itemIsFailed ? colors.danger.base : colors.orange.base,
                                    transition: "color 0.2s ease",
                                  }}
                                >
                                  Update
                                </Typography>
                                <EditOutlined
                                  className="update-text"
                                  sx={{
                                    fontSize: 14,
                                    color: isSuspended || itemIsFailed ? colors.danger.base : colors.orange.base,
                                    transition: "color 0.2s ease",
                                  }}
                                />
                              </Stack>
                            </Box>
                          }
                        >
                          <UpdatePaygPaymentModalContent
                            subscription={item?.subscription}
                            vault={itemVault}
                            companyId={companyId}
                            currentMethodLabel={billingMethodLabel}
                            queryClient={queryClient}
                            dispatch={dispatch}
                            setSnackbar={setSnackbar}
                          />
                        </BaseModalComponent>
                      </Box>

                      {Array.isArray(item?.subscription?.billingPeriods) && item.subscription.billingPeriods.length > 0 && (
                        <BaseModalComponent
                          modalKey={`secondary-subscription-history-${item?.subscription?.id}`}
                          modalLabel={`Payment History · ${getSubscriptionDisplayName(item?.subscription)}`}
                          defaultSizes={{ width: 820, height: 600 }}
                          elementTrigger={
                            <Button
                              variant="outlined"
                              startIcon={
                                <HistoryIcon
                                  sx={{ fontSize: 16, color: itemHasPending ? (itemIsFailed ? colors.danger.base : "#B45309") : undefined }}
                                />
                              }
                              sx={{
                                borderColor: itemHasPending ? (itemIsFailed ? colors.danger.base : "#F59E0B") : "#cbd5e1",
                                color: itemHasPending ? (itemIsFailed ? colors.danger.base : "#B45309") : "#475569",
                                bgcolor: itemHasPending ? (itemIsFailed ? alphaVar(colors.danger.base, 0.08) : "var(--fx-colors-yellow-100, #FFF9C2)") : undefined,
                                fontWeight: itemHasPending ? 800 : 500,
                                height: 35,
                                fontSize: "12px",
                                borderRadius: 2,
                                textTransform: "none",
                                boxShadow: itemHasPending
                                  ? `0 0 0 2px ${itemIsFailed ? alphaVar(colors.danger.base, 0.2) : "rgba(245, 158, 11, 0.3)"}`
                                  : undefined,
                                "&:hover": {
                                  borderColor: itemHasPending ? (itemIsFailed ? colors.danger.base : "#D97706") : "#94a3b8",
                                  bgcolor: itemHasPending ? (itemIsFailed ? alphaVar(colors.danger.base, 0.16) : "#FDE68A") : "var(--fx-surface-sunken, #F8FAFC)",
                                },
                              }}
                            >
                              Billing History ({item.subscription.billingPeriods.length}){" "}
                              {itemHasPending ? (itemIsFailed ? "· Payment Failed" : "· Action Needed") : ""}
                            </Button>
                          }
                        >
                          <SubscriptionPaymentHistoryModal subscription={subscriptionWithLineReference} />
                        </BaseModalComponent>
                      )}

                      <Button
                        variant="outlined"
                        onClick={() => setSubscriptionToCancel(item?.subscription)}
                        sx={{
                          borderColor: colors.orange.base,
                          color: colors.orange.base,
                          fontWeight: 500,
                          height: 35,
                          fontSize: "12px",
                          borderRadius: 2,
                          "&:hover": {
                            borderColor: colors.orange.base,
                            bgcolor: colors.orange[50],
                          },
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Card>
      )}

      {/*CANCEL */}
      {cancelledSubscriptions.length > 0 && (
        <Card
          sx={{
            mb: 2,
            borderRadius: 3,
            boxShadow: "none",
            overflow: "hidden",
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          }}
        >
          <Box sx={{ padding: 1.5, borderBottom: `1px solid ${alphaVar(colors.gray[400], 0.1)}` }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: colors.orange.base, alignItems: "center" }}>
              Subscriptions Canceled
            </Typography>
          </Box>

          <Box sx={{ p: 1.5 }}>
            {cancelledSubscriptions.map((item) => (
              <Accordion
                key={item?.subscription?.id}
                disableGutters
                elevation={0}
                sx={{
                  background: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: 1.5,
                  mb: 1,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: colors.orange.base }} />} sx={{ px: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", mr: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--fx-surface-textsecondary, #64748B)" }}>
                      {getSubscriptionDisplayName(item?.subscription)}
                    </Typography>
                    <CancelOutlined sx={{ color: colors.danger.base }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: "var(--fx-surface-base, #FFFFFF)", p: 0 }}>
                  <SubscriptionSection subscriptionItem={item?.subscription} onlyBenefits={true} />
                  {Array.isArray(item?.subscription?.billingPeriods) && item.subscription.billingPeriods.length > 0 && (
                    <Box sx={{ px: 1.5, pb: 1.5, display: "flex", justifyContent: "flex-end" }}>
                      <BaseModalComponent
                        modalKey={`secondary-subscription-history-${item?.subscription?.id}`}
                        modalLabel={`Payment History · ${getSubscriptionDisplayName(item?.subscription)}`}
                        defaultSizes={{ width: 820, height: 600 }}
                        elementTrigger={
                          <Button
                            variant="outlined"
                            startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                              color: "var(--fx-surface-textsecondary, #64748B)",
                              fontWeight: 500,
                              height: 35,
                              fontSize: "12px",
                              borderRadius: 2,
                              textTransform: "none",
                              "&:hover": {
                                borderColor: "var(--fx-surface-texttertiary, #94A3B8)",
                                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                              },
                            }}
                          >
                            Billing History ({item.subscription.billingPeriods.length})
                          </Button>
                        }
                      >
                        <SubscriptionPaymentHistoryModal subscription={item?.subscription} />
                      </BaseModalComponent>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Card>
      )}

      <Dialog open={!!subscriptionToCancel} onClose={handleCloseCancelDialog} disableEscapeKeyDown={isCancelling}>
        <DialogTitle sx={{ fontWeight: 700, color: colors.orange.base }}>Cancel Subscription?</DialogTitle>

        <DialogContent>
          <Typography sx={{ color: colors.gray[400] }}>Your access to this subscription will end immediately.</Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCancelDialog} disabled={isCancelling}>
            Discard
          </Button>

          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            disabled={isCancelling}
            sx={{
              bgcolor: colors.danger.base,
              color: "white",
              "&:hover": {
                bgcolor: colors.danger[100],
              },
            }}
          >
            {isCancelling ? "Cancelling..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
